import React, { useState, useMemo, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ReadingLog, Book } from '../lib/nocodb';
import { BookDetailsHeader } from './BookDetailsHeader';

interface ReadingLogListProps {
  readingLogs: (ReadingLog & { _localBookData?: Book })[];
  selectedBook?: Book | null;
  searchQuery?: string;
  onDeleteLog: (logId: number) => Promise<void>;
  onDeleteMultipleLogs: (logIds: number[]) => Promise<void>;
  onBookSelect?: (book: Book | null) => void;
  books?: Book[];
  onAddLog?: () => void;
  onDeleteBook?: () => void;
}

export function ReadingLogList({ readingLogs, selectedBook, searchQuery = '', onDeleteLog, onDeleteMultipleLogs, onBookSelect, books = [], onAddLog, onDeleteBook }: ReadingLogListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLogs, setSelectedLogs] = useState<Set<number>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single' | 'multiple' | 'book', id?: number } | null>(null);
  const [lightboxLog, setLightboxLog] = useState<(ReadingLog & { _localBookData?: Book }) | null>(null);
  const LOGS_PER_PAGE = 10;
  const TRUNCATE_LENGTH = 100;

  const getBookInfo = (log: ReadingLog & { _localBookData?: Book }) => {
    // First try to use local book data (for newly created logs)
    if (log._localBookData) {
      return {
        title: log._localBookData.title,
        author: log._localBookData.author,
        id: log._localBookData.Id,
        cover_url: log._localBookData.cover_url,
        publication_year: log._localBookData.publication_year
      };
    }
    
    // Fall back to API relationship data
    const bookData = log._nc_m2m_reading_log_books?.[0]?.books;
    if (bookData) {
      return {
        title: bookData.title,
        author: bookData.author,
        id: bookData.Id,
        cover_url: bookData.cover_url,
        publication_year: bookData.publication_year
      };
    }
    
    return {
      title: 'Unknown Book',
      author: 'Unknown Author',
      id: null,
      cover_url: undefined,
      publication_year: undefined
    };
  };

  // Helper function to strip markdown and get plain text
  const getPlainText = (markdown: string) => {
    return markdown
      .replace(/[#*`_~[\]()]/g, '') // Remove markdown syntax
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();
  };

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number) => {
    const plainText = getPlainText(text);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  // Check if text needs truncation
  const needsTruncation = (text: string) => {
    return getPlainText(text).length > TRUNCATE_LENGTH;
  };

  // Filter and search logic
  const filteredLogs = useMemo(() => {
    let filtered = [...readingLogs];

    // Sort by latest first (by CreatedAt date)
    filtered.sort((a, b) => {
      const dateA = new Date(a.CreatedAt || 0).getTime();
      const dateB = new Date(b.CreatedAt || 0).getTime();
      return dateB - dateA;
    });

    // Filter by selected book
    if (selectedBook) {
      filtered = filtered.filter(log => {
        const bookInfo = getBookInfo(log);
        return bookInfo.id === selectedBook.Id;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => {
        const notesMatch = log.notes?.toLowerCase().includes(query);
        const bookInfo = getBookInfo(log);
        const titleMatch = bookInfo.title.toLowerCase().includes(query);
        const authorMatch = bookInfo.author.toLowerCase().includes(query);
        return notesMatch || titleMatch || authorMatch;
      });
    }

    return filtered;
  }, [readingLogs, selectedBook, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / LOGS_PER_PAGE);
  const startIndex = (currentPage - 1) * LOGS_PER_PAGE;
  const paginatedLogs = filteredLogs.slice(startIndex, startIndex + LOGS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBook, searchQuery]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedLogs(new Set());
    setIsSelectionMode(false);
  }, [selectedBook, searchQuery]);

  const handleLogSelect = (logId: number, checked: boolean) => {
    setSelectedLogs(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(logId);
      } else {
        newSet.delete(logId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLogs(new Set(paginatedLogs.map(log => log.Id)));
    } else {
      setSelectedLogs(new Set());
    }
  };

  const handleDeleteSingle = async (logId: number) => {
    try {
      await onDeleteLog(logId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete log:', error);
    }
  };

  const handleDeleteMultiple = async () => {
    try {
      await onDeleteMultipleLogs(Array.from(selectedLogs));
      setSelectedLogs(new Set());
      setIsSelectionMode(false);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete logs:', error);
    }
  };

  const getSelectedBookLogs = () => {
    if (!selectedBook) return [];
    return filteredLogs.filter(log => {
      const bookInfo = getBookInfo(log);
      return bookInfo.id === selectedBook.Id;
    });
  };

  const handleDeleteByBook = async () => {
    const bookLogs = getSelectedBookLogs();
    const bookLogIds = bookLogs.map(log => log.Id);
    try {
      await onDeleteMultipleLogs(bookLogIds);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete logs by book:', error);
    }
  };

  const handleBookTitleClick = (bookInfo: any) => {
    if (onBookSelect && bookInfo.id) {
      // Find the book in the books array
      const book = books.find(b => b.Id === bookInfo.id);
      if (book) {
        onBookSelect(book);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Book Details Header when book is selected */}
      {selectedBook ? (
        <BookDetailsHeader
          book={selectedBook}
          onAddLog={() => onAddLog?.()}
          onDeleteBook={() => onDeleteBook?.()}
          onUnselectBook={() => onBookSelect?.(null)}
        />
      ) : (
        /* Default header when viewing all books */
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">My Reading Logs</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {filteredLogs.length} {filteredLogs.length === 1 ? 'note' : 'notes'}
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </span>
          </div>
        </div>
      )}

      {/* Show message when no logs found */}
      {filteredLogs.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          {selectedBook && searchQuery ? (
            <p>No notes found for "{searchQuery}" in {selectedBook.title}.</p>
          ) : selectedBook ? (
            <p>No reading logs for {selectedBook.title} yet. Click "Add Reading Log" above to get started!</p>
          ) : searchQuery ? (
            <p>No notes found for "{searchQuery}".</p>
          ) : (
            <p>No reading logs yet.</p>
          )}
        </div>
      )}

      {/* Stats and Selection Mode Toggle - only show when there are logs */}
      {filteredLogs.length > 0 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedBook && (
              <>
                {filteredLogs.length} {filteredLogs.length === 1 ? 'note' : 'notes'}
                {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
              </>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Selection Mode Toggle */}
            <button
              onClick={() => {
                setIsSelectionMode(!isSelectionMode);
                setSelectedLogs(new Set());
              }}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
            >
              {isSelectionMode ? 'Cancel' : 'Select'}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar - only show when there are logs and in selection mode */}
      {filteredLogs.length > 0 && isSelectionMode && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedLogs.size === paginatedLogs.length && paginatedLogs.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium">
                  Select All ({selectedLogs.size} selected)
                </span>
              </label>
            </div>
            
            <div className="flex gap-2">
              {selectedBook && (
                <button
                  onClick={() => setDeleteConfirm({ type: 'book', id: undefined })}
                  className="px-3 py-1 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600"
                  disabled={getSelectedBookLogs().length === 0}
                >
                  Delete All in "{selectedBook.title}" ({getSelectedBookLogs().length})
                </button>
              )}
              
              {selectedLogs.size > 0 && (
                <button
                  onClick={() => setDeleteConfirm({ type: 'multiple', id: undefined })}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Delete Selected ({selectedLogs.size})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reading Log Cards - only show when there are logs */}
      {filteredLogs.length > 0 && (
        <div className="space-y-4">
        {paginatedLogs.map((log) => {
          const bookInfo = getBookInfo(log);
          return (
            <div key={log.Id} className={`p-5 bg-white rounded-xl shadow-md ${isSelectionMode ? 'border-2 border-dashed border-gray-200' : ''}`}>
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3 flex-1">
                  {/* Checkbox in selection mode */}
                  {isSelectionMode && (
                    <input
                      type="checkbox"
                      checked={selectedLogs.has(log.Id)}
                      onChange={(e) => handleLogSelect(log.Id, e.target.checked)}
                      className="mt-1 rounded"
                    />
                  )}
                  
                  {/* Book cover - show for all logs when not in selected book mode */}
                  {!selectedBook && bookInfo.cover_url && (
                    <div className="flex-shrink-0">
                      <img
                        src={bookInfo.cover_url}
                        alt={`${bookInfo.title} cover`}
                        className="w-12 h-18 object-cover rounded border bg-gray-100"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    {!selectedBook && (
                      <>
                        <div className="flex items-start gap-2">
                          <div>
                            {onBookSelect && bookInfo.id ? (
                              <button
                                onClick={() => handleBookTitleClick(bookInfo)}
                                className="text-xl font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                                title="Click to view all logs for this book"
                              >
                                {bookInfo.title}
                              </button>
                            ) : (
                              <h3 className="text-xl font-bold text-gray-900">{bookInfo.title}</h3>
                            )}
                            {bookInfo.author !== 'Unknown Author' && (
                              <p className="text-sm text-gray-500">by {bookInfo.author}</p>
                            )}
                            {bookInfo.publication_year && (
                              <p className="text-xs text-gray-400">Published: {bookInfo.publication_year}</p>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="mt-3">
                      {log.notes && (
                        <div>
                          {needsTruncation(log.notes) ? (
                            <div>
                              <p className="text-gray-700 text-sm leading-relaxed">
                                {truncateText(log.notes, TRUNCATE_LENGTH)}
                              </p>
                              <button
                                onClick={() => setLightboxLog(log)}
                                className="mt-2 text-sm text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                                Read full note
                              </button>
                            </div>
                          ) : (
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {getPlainText(log.notes)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">
                    {log.CreatedAt ? new Date(log.CreatedAt).toLocaleString() : 'Unknown date'}
                  </span>
                  
                  {/* Individual delete button */}
                  {!isSelectionMode && (
                    <button
                      onClick={() => setDeleteConfirm({ type: 'single', id: log.Id })}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Delete this log"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Pagination Controls - only show when there are logs */}
      {filteredLogs.length > 0 && totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  page === currentPage
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {deleteConfirm.type === 'single' ? 'Delete Reading Log' : 'Delete Multiple Logs'}
            </h3>
            
            <p className="text-gray-600 mb-6">
              {deleteConfirm.type === 'single' 
                ? 'Are you sure you want to delete this reading log? This action cannot be undone.'
                : deleteConfirm.type === 'book' && selectedBook
                ? `Are you sure you want to delete all ${getSelectedBookLogs().length} reading logs for "${selectedBook.title}"? This action cannot be undone.`
                : `Are you sure you want to delete ${selectedLogs.size} selected reading logs? This action cannot be undone.`
              }
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'single' && deleteConfirm.id) {
                    handleDeleteSingle(deleteConfirm.id);
                  } else if (deleteConfirm.type === 'book') {
                    handleDeleteByBook();
                  } else {
                    handleDeleteMultiple();
                  }
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal for Full Note Viewing */}
      {lightboxLog && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {(() => {
                    const bookInfo = getBookInfo(lightboxLog);
                    return bookInfo.title !== 'Unknown Book' ? bookInfo.title : 'Reading Log';
                  })()}
                </h3>
                <p className="text-sm text-gray-500">
                  {lightboxLog.CreatedAt ? new Date(lightboxLog.CreatedAt).toLocaleString() : 'Unknown date'}
                </p>
              </div>
              <button
                onClick={() => setLightboxLog(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom styling for markdown elements
                    h1: ({node, ...props}) => <h1 className="text-2xl font-bold mb-4 text-gray-900" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-xl font-semibold mb-3 text-gray-900" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-lg font-medium mb-2 text-gray-900" {...props} />,
                    p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-3 text-gray-700" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-3 text-gray-700" {...props} />,
                    li: ({node, ...props}) => <li className="mb-1" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-200 pl-4 italic text-gray-600 mb-3" {...props} />,
                    code: ({node, ...props}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props} />,
                    pre: ({node, ...props}) => <pre className="bg-gray-100 p-3 rounded overflow-x-auto mb-3" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                    em: ({node, ...props}) => <em className="italic" {...props} />,
                    a: ({node, ...props}) => <a className="text-blue-500 hover:text-blue-700 underline" {...props} />,
                  }}
                >
                  {lightboxLog.notes || ''}
                </ReactMarkdown>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {getPlainText(lightboxLog.notes || '').length} characters
              </div>
              <button
                onClick={() => setLightboxLog(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
